import React from 'react';
import { Map, Marker } from 'google-maps-react';
import { useGoogle } from '@iso/lib/helpers/googleApiHelper';
import { attachGoogleNorthArrow } from '@iso/lib/helpers/mapFurniture';
import { priorityMeta, TB_COLORS } from './utils';

const DEFAULT_CENTER = { lat: -25.3444, lng: 131.0369 };

const hasCoords = (t) => {
  const lat = parseFloat(t.lat);
  const lng = parseFloat(t.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

const MapView = ({ tasks, areaMap, propertyName, onEdit, propertyCenter }) => {
  const { google, failed } = useGoogle();
  const located = tasks.filter(hasCoords);
  const center = located.length
    ? {
        lat: located.reduce((s, t) => s + parseFloat(t.lat), 0) / located.length,
        lng: located.reduce((s, t) => s + parseFloat(t.lng), 0) / located.length,
      }
    : propertyCenter || DEFAULT_CENTER;

  const shell = (children) => (
    <div>
      {located.length < tasks.length ? (
        <div style={{ color: TB_COLORS.subtext, fontSize: 13, marginBottom: 8 }}>
          Showing {located.length} of {tasks.length} tasks with a location.
        </div>
      ) : null}
      <div
        style={{
          height: 'calc(100vh - 320px)',
          minHeight: 420,
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid #eaecf0',
          position: 'relative',
        }}
      >
        {children}
      </div>
    </div>
  );

  if (!google) {
    return shell(
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: TB_COLORS.subtext }}>
        {failed ? 'Map unavailable — the Google Maps script could not be loaded.' : 'Loading map…'}
      </div>
    );
  }

  // Coloured dot per priority; click a pin to open the task (native title
  // tooltip shows the task name on hover — google-maps-react InfoWindow event
  // handlers are unreliable, so we open the task directly instead).
  const dot = (color) => ({
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: 7,
  });

  return shell(
    <Map
      google={google}
      zoom={located.length ? 12 : 5}
      initialCenter={center}
      mapType="hybrid"
      streetViewControl={false}
      mapTypeControl
      fullscreenControl={false}
      scaleControl
      onReady={(mapProps, map) => attachGoogleNorthArrow(map, mapProps.google)}
      containerStyle={{ position: 'relative', width: '100%', height: '100%' }}
      style={{ width: '100%', height: '100%' }}
    >
      {located.map((t) => {
        const area = areaMap?.[t.primary_object_id];
        const title = [t.title, [propertyName, area].filter(Boolean).join(' · ')]
          .filter(Boolean)
          .join('\n');
        return (
          <Marker
            key={t.id}
            position={{ lat: parseFloat(t.lat), lng: parseFloat(t.lng) }}
            title={title}
            icon={dot(priorityMeta(t.priority).color)}
            onClick={() => onEdit(t)}
          />
        );
      })}
    </Map>
  );
};

export default MapView;
